import { Pencil, Trash2 } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	IconButton,
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import {
	Modal,
	ModalStickyFooter,
} from "src/components/Modals/Components/ModalsComponents";
import { FlashcardCard } from "src/components/NewComponents/Flashcards/FlashcardCard";
import isMobile from "src/helpers/isMobile";
import { FlashcardEditor, type FlashcardEditorHandle } from "./FlashcardEditor";

export interface EditableFlashcardData {
	id?: string | number;
	front: string;
	back: string;
	key?: number;
}

export interface EditableFlashcardCardProps {
	flashcard: EditableFlashcardData;
	isEditing: boolean;
	onStartEdit: () => void;
	onCancelEdit: () => void;
	onSave: (data: { front: string; back: string }) => void | Promise<void>;
	onDelete: () => void | Promise<void>;
	disableEdit?: boolean;
	disableEditMessage?: string;
	isSaving?: boolean;
	isDeleting?: boolean;
	query?: string;
	testId?: string;
}

export function EditableFlashcardCard({
	flashcard,
	isEditing,
	onStartEdit,
	onCancelEdit,
	onSave,
	onDelete,
	disableEdit,
	disableEditMessage,
	isSaving,
	isDeleting,
	query,
	testId,
}: EditableFlashcardCardProps) {
	const { t } = useTranslation();
	const cardKey = flashcard.key ?? flashcard.id ?? "new";
	const mobile = isMobile();
	const editorRef = useRef<FlashcardEditorHandle>(null);

	if (isEditing && !mobile) {
		return (
			<FlashcardEditor
				keySuffix={cardKey}
				initialFront={flashcard.front}
				initialBack={flashcard.back}
				onSave={onSave}
				onCancel={onCancelEdit}
				isSaving={isSaving}
				isEditing
			/>
		);
	}

	return (
		<div data-test-id={testId}>
			<FlashcardCard
				front={flashcard.front}
				back={flashcard.back}
				query={query}
				actions={
					<>
						{!disableEdit && (
							<Tooltip title={t("decks.editFlashcard", "Edytuj fiszkę")}>
								<IconButton
									isActive
									onClick={onStartEdit}
									disabled={isSaving || isDeleting}
									aria-label={t("decks.editFlashcard", "Edytuj fiszkę")}
									data-test-id={`flashcard-edit-button-${cardKey}`}
								>
									<Pencil className="icon-small icon-primary" />
								</IconButton>
							</Tooltip>
						)}
						<Tooltip title={t("decks.deleteFlashcard", "Usuń fiszkę")}>
							<IconButton
								className="icon-button--error"
								onClick={onDelete}
								disabled={isSaving || isDeleting}
								aria-label={t("decks.deleteFlashcard", "Usuń fiszkę")}
								data-test-id={`flashcard-delete-button-${cardKey}`}
							>
								<Trash2 className="icon-small icon-error" />
							</IconButton>
						</Tooltip>
					</>
				}
			/>
			{disableEdit && disableEditMessage && (
				<span className="size-12-light text-grey">{disableEditMessage}</span>
			)}
			{isEditing && mobile && (
				<Modal onClose={onCancelEdit} className="flashcard-mobile-edit-modal">
					<FlashcardEditor
						ref={editorRef}
						bare
						hideFooter
						keySuffix={cardKey}
						initialFront={flashcard.front}
						initialBack={flashcard.back}
						onSave={onSave}
						onCancel={onCancelEdit}
						isSaving={isSaving}
					/>
					<ModalStickyFooter className="items-center justify-end pt-8">
						<TextPrimaryButton
							onClick={onCancelEdit}
							disabled={isSaving}
							data-test-id={`flashcard-edit-mobile-cancel-${cardKey}`}
						>
							{t("commands.cancel")}
						</TextPrimaryButton>
						<PrimaryButton
							onClick={() => editorRef.current?.save()}
							disabled={isSaving}
							data-test-id={`flashcard-edit-mobile-save-${cardKey}`}
						>
							{t("commands.save")}
						</PrimaryButton>
					</ModalStickyFooter>
				</Modal>
			)}
		</div>
	);
}
